// src/utils/ga4.js
// Google Analytics 4 — 환경변수 VITE_GA4_MEASUREMENT_ID 있을 때만 활성.
// 정적 gtag snippet 은 vite.config.js 의 gaInjector 가 index.html 에 주입함.
// (Google 자동 검증이 정적 HTML 만 보므로 static 주입이 필요)
// 이 모듈은 SPA 라우트 전환 page_view + 비즈니스 이벤트만 담당.

const MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;
const LS_FLAG = 'cc.ga_internal'; // admin/내부 트래픽 제외 플래그 (localStorage 영속)

// 내부(admin) 트래픽이면 GA4 집계에서 제외. 모든 page_view 가 trackPageView 에서만 나오므로
// 이 플래그로 page_view + 모든 비즈니스 이벤트를 차단하면 GA4 활성 사용자/이벤트에서 빠진다.
let internal = false;
if (typeof window !== 'undefined') {
  try { internal = localStorage.getItem(LS_FLAG) === '1'; } catch { /* noop */ }
  // 다음 로드부터는 모듈 로드 시점에 즉시 비활성 (gtag 공식 옵트아웃 플래그)
  if (internal && MEASUREMENT_ID) window[`ga-disable-${MEASUREMENT_ID}`] = true;
}

export function initGA() {
  // static script 가 빌드 시 주입되어 자동 로드 — JS 측 추가 작업 불필요.
  // 호환성 stub.
}

// 로그인 사용자가 admin 이면 setGAInternal(true) — GA4 에서 본인 트래픽 분리.
export function setGAInternal(isInternal) {
  internal = !!isInternal;
  try { localStorage.setItem(LS_FLAG, internal ? '1' : '0'); } catch { /* noop */ }
  if (MEASUREMENT_ID) window[`ga-disable-${MEASUREMENT_ID}`] = internal;
}

// 봇 필터 — gtag snippet 은 send_page_view:false 이므로 모든 page_view 가 이 함수로만 나간다.
// 즉 여기서 거르면 GA4 집계가 정화된다. 취약점 스캐너는 React SPA 에 없는 경로(/login.php, /home,
// /en, /wp-* 등)를 prove → SPA fallback 이 index.html(200) 을 주고 gtag 가 발사돼 "활성 사용자"가
// 봇으로 부풀려진다(2026-06-15 리포트: US 400 vs KR 36, top page 에 존재하지 않는 /login.php).
// → 실제 App.jsx 라우트 화이트리스트에만 page_view 를 보낸다. 새 라우트 추가 시 이 목록도 갱신할 것.
const REAL_ROUTES = [
  /^\/$/, /^\/login$/, /^\/register$/, /^\/pricing$/, /^\/privacy$/, /^\/terms$/,
  /^\/dashboard$/, /^\/create$/, /^\/account$/, /^\/admin$/, /^\/video\/[^/]+$/,
];

export function trackPageView(path) {
  if (internal || !window.gtag) return;
  // 쿼리/해시 제거 후 실제 라우트인지 검사 — 봇 probe 경로면 집계 제외.
  const clean = (path || '/').split(/[?#]/)[0];
  if (!REAL_ROUTES.some((re) => re.test(clean))) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  });
}

// 비즈니스 이벤트 — 마케팅 funnel 측정용
export function trackEvent(name, params = {}) {
  if (internal || !window.gtag) return;
  window.gtag('event', name, params);
}

// 사전 정의된 표준 이벤트 헬퍼
export const ga = {
  signUp: (method = 'google') => trackEvent('sign_up', { method }),
  videoStart: (stance, country) => trackEvent('video_render_start', { stance, country }),
  videoComplete: (videoId) => trackEvent('video_render_complete', { video_id: videoId }),
  purchase: (tier, valueUsd) => trackEvent('purchase', {
    currency: 'USD',
    value: valueUsd,
    items: [{ item_id: tier, item_name: tier }],
  }),
  planChange: (fromTier, toTier) => trackEvent('plan_change', { from: fromTier, to: toTier }),
  share: (videoId, channel) => trackEvent('share', { video_id: videoId, method: channel }),
};
